import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import StandardInput from 'common/components/StandardInput/StandardInput';
import { useStores } from 'common/hooks/useStores';
import DattoERDLogo from 'common/icons/DattoEDRLogo';
import DattoSmallLogo from 'common/icons/DattoSmallLogo';
import KaseyaOneLoginBottomLeftWave from 'common/icons/KaseyaOneLoginBottomLeftWave';
import KaseyaOneLoginLogo from 'common/icons/KaseyaOneLoginLogo';
import KaseyaOneLoginTopRightWave from 'common/icons/KaseyaOneLoginTopRightWave';
import KaseyaOneSmallLogo from 'common/icons/KaseyaOneSmallLogo';
import Cookies from 'js-cookie';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import './login.scss';
import { useLocation } from 'react-router';
import log from 'services/helpers/log';
import { IAuthSettings } from 'stores/AuthStore';

const KASEYA_ONE_HELP_URL = 'https://kaseya1523304719.zendesk.com/hc/en-gb';
const DATTO_EDR_HELP_URL = 'https://edr.datto.com/help/Content/1-start/home.htm';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string>();
    const { authStore } = useStores();
    const location = useLocation();

    const { ssoEnabled, dattoAuthorizationUrl, k1AuthorizationUrl, authenticationMethod } = authStore.settings as IAuthSettings;

    if (location && location.state && location.state.redirect) {
        sessionStorage.setItem('redirectUri', location.state.redirect);
    }

    let errorMessage = error || location?.state?.error;

    async function onSubmit(e: Event) {
        e.preventDefault();

        try {
            await authStore.login(username, password);
        } catch (error: any) {
            switch (error.status) {
                case 401:
                    setError('Invalid username or password');
                    break;
                case 403:
                    setError(error.message);
                    break;
                default:
                    log.error(error);
                    setError('Something went wrong. Please refresh your browser and try again');
            }
        }
    }

    useEffect(() => {
        const loginType = Cookies.get('loginType');

        if (loginType === 'logout') {
            return;
        }

        if (loginType === 'kaseya1' && k1AuthorizationUrl) {
            const url = new URL(k1AuthorizationUrl as string);
            const state = url.searchParams.get('state');
            sessionStorage.setItem('k1State', state || '');

            window.location.href = k1AuthorizationUrl as string;
        } else if (loginType === 'authweb' || (!k1AuthorizationUrl && authenticationMethod !== 'local')) {
            window.location.href = dattoAuthorizationUrl;
        }
    }, []);

    return (
        <div className="login-container">
            <div className="top-right-wave">
                <KaseyaOneLoginTopRightWave />
            </div>

            <div className="bottom-left-wave">
                <KaseyaOneLoginBottomLeftWave />
            </div>

            <div className="c-logo--container-kaseya">
                <KaseyaOneLoginLogo />
            </div>

            <div className="login">
                <div className="login-insider">
                    <form onSubmit={(e) => onSubmit(e)}>
                        <div className="u-flex u-flex-center u-margin-lg-bottom">
                            <div className="login__logo">
                                <DattoERDLogo />
                            </div>
                        </div>

                        {errorMessage && (
                            <Alert
                                severity="error"
                                className="login__margin_botom_sm"
                                icon={<FontAwesomeIcon icon={faTriangleExclamation} className={'c-icon c-icon--regular'} />}
                                onClose={() => {
                                    setError('');
                                    errorMessage = null;
                                }}
                            >
                                {errorMessage}
                            </Alert>
                        )}

                        {authenticationMethod === 'local' && (
                            <>
                                <StandardInput fullWidth label="Username" value={username} onChange={(e) => setUsername(e.target.value)} />

                                <StandardInput type="password" label="Password" value={password} fullWidth onChange={(e) => setPassword(e.target.value)} />

                                <div className="login__margin_botom_sm"></div>

                                <button className="c-button c-button--primary c-button--full login__button" type="submit">
                                    Log In
                                </button>

                                <Divider className="login__margin_botom_sm">Or</Divider>
                            </>
                        )}

                        <button
                            className="c-button c-button--secondary c-button-icon-before c-button--full login__button"
                            type="button"
                            disabled={!k1AuthorizationUrl}
                            onClick={() => {
                                // extract state query param from k1AuthorizationUrl and store it in session storage

                                const url = new URL(k1AuthorizationUrl as string);
                                const state = url.searchParams.get('state');
                                sessionStorage.setItem('k1State', state || '');

                                window.location.href = k1AuthorizationUrl as string;
                            }}
                        >
                            <div className="u-flex u-flex-center">
                                <div className="c-icon--container-kaseya u-margin-sm-right">
                                    <KaseyaOneSmallLogo className={'c-icon--xsmall'} />
                                </div>
                                Log In with KaseyaOne
                            </div>
                        </button>

                        <button
                            className="c-button c-button--secondary c-button-icon-before c-button--full login__button"
                            type="button"
                            onClick={() => {
                                window.location.href = dattoAuthorizationUrl;
                            }}
                        >
                            <div className="u-flex u-flex-center">
                                <div className="c-icon--container-datto u-margin-sm-right">
                                    <DattoSmallLogo className={'c-icon--xsmall'} />
                                </div>
                                Log In with Datto
                            </div>
                        </button>

                        <a href={KASEYA_ONE_HELP_URL} target="_blank" rel="noreferrer" className="c-link c-link--primary u-flex u-flex-center u-padding-xs-top">
                            Learn More about KaseyaOne
                        </a>
                    </form>
                    <div hidden={!ssoEnabled}></div>
                </div>
                <div className="u-flex u-flex-center u-medium login-footer">
                    <a href={DATTO_EDR_HELP_URL} target="_blank" rel="noreferrer" className="c-link">
                        Help & Support
                    </a>
                    <span className="u-margin-sm-left u-margin-sm-right">|</span>
                    <span>© Kaseya {new Date().getFullYear()}</span>
                </div>
            </div>
        </div>
    );
};

export default observer(Login);
